import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './ReconRow.module.scss';
import ReconTable from './ReconTable';
import StatusChip from './StatusChip';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import { caretRight, infoToolTip } from '../../../../../assets/hostedassets';
import { splitReconLabel } from './reconLabel';
import { useReconExpand } from '../ReconExpandContext';
import { exactScoreTitle, formatReconScore } from './formatReconScore';
import { PROVISIONAL_FOOTNOTE, PROVISIONAL_FOOTNOTE_NOTE, RECON_TOOLTIPS, SOURCE_TOGGLE_CAPTIONS } from '../constants/reconciliationsCopy';
import type { Recon, ReconStatus } from '../../../../../store/reconciliations/types';

const SCORE_CLASS: Record<ReconStatus, string> = {
    RECONCILED: styles.ScoreGreen,
    PARTIAL: styles.ScoreYellow,
    NOT_RECONCILED: styles.ScoreRed,
    NA: styles.ScoreNotAvailable,
    ANCHOR: styles.ScoreNotAvailable,
};

interface ReconRowProps {
    orgId: string;
    fys: number[] | null;
    recon: Recon;
}

/** One row per recon, independently expandable — NOT a shared "which one is open" boolean
 * — each mounted ReconRow owns its own `expanded` state. */
function ReconRow({ orgId, fys, recon }: Readonly<ReconRowProps>) {
    const [expanded, setExpanded] = useState(false);
    // The body mounts on first open and then stays mounted (hidden) so there is an element to
    // transition on close; ReconTable is heavy, so it must not mount before that first open.
    const [hasOpened, setHasOpened] = useState(false);
    // Lags `expanded` by a frame on open: the body mounts collapsed, then opens, so the grid
    // track has a painted 0fr to animate away from.
    const [bodyOpen, setBodyOpen] = useState(false);
    const [scoredToggleOption, setScoredToggleOption] = useState(recon.sourceToggle?.default ?? null);

    useEffect(() => {
        if (!expanded) {
            setBodyOpen(false);
            return undefined;
        }
        const frame = requestAnimationFrame(() => setBodyOpen(true));
        return () => cancelAnimationFrame(frame);
    }, [expanded]);

    // Acts on a header Expand/Collapse All only for commands issued after this row mounted.
    const { command, seq, report, forget } = useReconExpand();
    const handledSeq = useRef(seq);
    useEffect(() => {
        if (seq === handledSeq.current) return;
        handledSeq.current = seq;
        const expand = command === 'EXPAND';
        if (expand) setHasOpened(true);
        setExpanded(expand);
    }, [seq, command]);

    useEffect(() => {
        report(recon.id, expanded);
    }, [report, recon.id, expanded]);
    useEffect(() => () => forget(recon.id), [forget, recon.id]);

    const toggleExpanded = () => {
        setHasOpened(true);
        setExpanded((prev) => !prev);
    };

    const hasProvisionalFootnote = recon.notes?.includes(PROVISIONAL_FOOTNOTE_NOTE);

    // Read as ONE object so a null score can never borrow the other option's number. The
    // server's roll-up is worst-of across every returned FY, which rows[0] alone is not.
    const activeOption = recon.sourceToggle?.options.find((option) => option.id === scoredToggleOption);
    const display = activeOption?.rollup ?? { score: recon.score, status: recon.status };
    const { name, qualifier } = splitReconLabel(recon.label);

    return (
        <div className={styles.ReconRow}>
            <button type="button" className={styles.Header} onClick={toggleExpanded} aria-expanded={expanded}>
                <img src={caretRight} alt="" className={clsx(styles.Chevron, { [styles.ChevronExpanded]: expanded })} />
                <span className={styles.Label}>
                    {name}
                    {qualifier && <span className={styles.SubLabel}> {qualifier}</span>}
                    {recon.subLabel && <span className={styles.SubLabel}> {recon.subLabel}</span>}
                </span>
                <Tooltip content={RECON_TOOLTIPS[recon.id] ?? recon.label} direction="top" usePortal fitContent>
                    <img src={infoToolTip} height="14px" alt="" className={styles.InfoIcon} />
                </Tooltip>
                {recon.sourceToggle && (
                    <div className={styles.SourceToggle} onClick={(e) => e.stopPropagation()}>
                        {SOURCE_TOGGLE_CAPTIONS[recon.sourceToggle.key] && (
                            <span className={styles.SourceToggleCaption}>{SOURCE_TOGGLE_CAPTIONS[recon.sourceToggle.key]}</span>
                        )}
                        <CategorySelector
                            categoryOptions={recon.sourceToggle.options}
                            selectedCategory={scoredToggleOption}
                            changeSelectedTab={setScoredToggleOption}
                            categoryStyle={{ padding: '0.125rem 1rem' }}
                        />
                    </div>
                )}
                <span className={styles.Spacer} />
                <StatusChip status={display.status} />
                <span
                    className={clsx(styles.Score, SCORE_CLASS[display.status])}
                    title={display.score === null ? undefined : exactScoreTitle(display.score)}
                    aria-label={display.score === null ? undefined : exactScoreTitle(display.score)}
                >
                    {display.score === null ? 'N/A' : `= ${formatReconScore(display.score)}`}
                </span>
            </button>
            {hasOpened && (
                <div className={bodyOpen ? styles.BodyWrapOpen : styles.BodyWrap}>
                    <div className={styles.BodyClip}>
                        <div className={styles.Body}>
                            <ReconTable orgId={orgId} fys={fys} recon={recon} activeToggleOptionId={scoredToggleOption} />
                            {hasProvisionalFootnote && <div className={styles.ProvisionalFootnote}>{PROVISIONAL_FOOTNOTE}</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReconRow;
